import { DataTable } from '@pixel/core/DataTable';
import { styled } from '@pixel/core/styles';
import ViewContextTableIcon from '@pixel/assets/icons/ViewContextTable';
import Typography from '@pixel/core/Typography';
import React, { FC, useState } from 'react';
import LocalizedTypography from '../../components/LocalizedTypography';
import { LocalizedText } from '../../contexts/Localization/LocalizationTypes';
import useVersionLogListQuery from '../hooks/useVersionLogListQuery';
import {VersionLogExpandingArea, VersionLogSearchResult} from '../types/VersionLog';
import {useLocalizedText, useMultipleLocalizedText} from '../../contexts/Localization/localizationHooks';
import { Link } from '@mui/material';
import { ListBase } from '@pixel/core';
import ListBaseSkeletonLoading from "../../components/ListBaseDefaultLoading";
import moment from "moment";

interface VersionLogSectionProps {
    selectedAutomationId: number;
}

const CellContainer = styled('div')({
    maxWidth: '180px',
    wordWrap: 'break-word',
    whiteSpace: 'normal'
})

const VersionLogSectionContainer = styled('div')`
  padding: ${({ theme }: any) => theme.spacing(3)};
  background-color: white;
  border-radius: 8px;
  
  .MuiToolbar-root, div[data-qa='ListBaseHeaderContainer'] {
    display: none;
  }
  
  .MuiTableContainer-root {
    margin-bottom: 0;
    box-shadow: none;
    tr:nth-child(even):not(:last-of-type) td {
      border-bottom: 1px solid rgb(228, 237, 243);
    }
    tr:nth-child(odd) {
      height: 56px;
    }
  }

  .MuiPaper-root {
    box-shadow: none;
  }

  .expand-collapse-row-trigger {
    text-decoration: none;
    &:hover, &:focus {
      text-decoration: underline;
    }
  }
`;

const ALL_POSSIBLE_MATCHES = [
    LocalizedText.AUTOMATION_FILTER_REQ_CATEGORY,
    LocalizedText.AUTOMATION_FILTER_REQ_DEPARTMENT,
    LocalizedText.AUTOMATION_FILTER_REQ_JOB_TYPE,
    LocalizedText.AUTOMATION_FILTER_REQ_JOB_LOCATION,
    LocalizedText.AUTOMATION_FILTER_REQ_STATUS,
    LocalizedText.AUTOMATION_FILTER_APPLICATION_ACTIVITY,
    LocalizedText.AUTOMATION_FILTER_REQ_WORKFLOW,
    LocalizedText.AUTOMATION_FILTER_ACTIVE_APPLICATIONS,
    LocalizedText.AUTOMATION_FILTER_COMPANY_NAME,
    LocalizedText.AUTOMATION_ACTION_FIELD_CANDIDATE_EMAIL_TEMPLATE_ID,
    LocalizedText.AUTOMATION_ACTION_FIELD_CANDIDATE_WORKFLOW_STATE_TITLE,
    LocalizedText.AUTOMATION_ACTION_FIELD_CANDIDATE_DISPOSITION_ID,
    LocalizedText.AUTOMATION_FILTER_CANDIDATE_WORKFLOW_STATE,
    LocalizedText.AUTOMATION_FILTER_CANDIDATE_DAYS_ON_WORKFLOW_STATE,
    LocalizedText.AUTOMATION_TRIGGER_CANDIDATE_WORKFLOW_STATE,
    LocalizedText.AUTOMATION_FORM_COMPANY_FILTERS,
    LocalizedText.REQ_STATUS_CHANGE,
    LocalizedText.CANDIDATE_WORKFLOW_STATE,
    LocalizedText.AUTOMATION_FORM_NAME,
    LocalizedText.AUTOMATION_FORM_TYPE,
    LocalizedText.AUTOMATION_FORM_SERVICE_ACCOUNT_USER,
    LocalizedText.AUTOMATION_FORM_TRIGGER,
    LocalizedText.AUTOMATION_FORM_COMPANY_FILTERS,
    LocalizedText.AUTOMATION_FORM_COMPANY_ACTIONS,
    LocalizedText.AUTOMATION_FORM_LINKED_AUTOMATIONS,
];

const versionLogTranslationMap = {
    'AUTOMATION_FORM_NAME': LocalizedText.AUTOMATION_FORM_NAME,
    'AUTOMATION_FORM_TYPE': LocalizedText.AUTOMATION_FORM_TYPE,
    'AUTOMATION_FORM_SERVICE_ACCOUNT_USER': LocalizedText.AUTOMATION_FORM_SERVICE_ACCOUNT_USER,
    'AUTOMATION_FORM_TRIGGER': LocalizedText.AUTOMATION_FORM_TRIGGER,
    'AUTOMATION_FORM_COMPANY_FILTERS': LocalizedText.AUTOMATION_FORM_COMPANY_FILTERS,
    'AUTOMATION_FORM_COMPANY_ACTIONS': LocalizedText.AUTOMATION_FORM_COMPANY_ACTIONS,
    'AUTOMATION_FORM_LINKED_AUTOMATIONS': LocalizedText.AUTOMATION_FORM_LINKED_AUTOMATIONS,
} as {
    [key in VersionLogExpandingArea['fieldName']]: LocalizedText
}

const translateDisplayValues = (values: any) => {

    if (typeof values !== 'string') {
        return '';
    }

    const translationsMap: Record<string, string> = {};

    ALL_POSSIBLE_MATCHES.forEach((match) => {
        const translation = useLocalizedText(match);
        if (translation) {
            translationsMap[match] = translation;
        }
    });

    let translatedString = values;
    Object.keys(translationsMap).forEach((match) => {
        translatedString = translatedString.replace(new RegExp(`{${match}}`, 'g'), translationsMap[match]);
    });

    return translatedString;
};

const getFormattedDate = (date: Date | string): string => moment(date).format('YYYY/MM/DD hh:mm A Z');


const VersionLogDataTableColumns= [
    {
        id: 'actionUserName',
        columnName: window.i18n.automationEngine.AUTOMATION_VERSION_LOG_HEADER_USER,
        minWidth: 0,
        renderCell: (node: VersionLogSearchResult) => <Typography data-id={`versionLog-${node.automationRecordAuditId}-actionUserName`}>{node.actionUserName}</Typography>
    },
    {
        id: 'modifiedDate',
        columnName: window.i18n.automationEngine.AUTOMATION_VERSION_LOG_HEADER_MODIFIED_DATE,
        minWidth: 0,
        renderCell: (node: VersionLogSearchResult) =>
            <Typography  data-id={`versionLog-${node.automationRecordAuditId}-modifiedDate`}>{getFormattedDate(node.modifiedDate)}</Typography>
    },
];

const VersionLogDataTableExpandedColumns = [
    {
        id: 'fieldName',
        columnName: window.i18n.automationEngine.AUTOMATION_VERSION_LOG_HEADER_FIELD_LABEL,
        minWidth: 0,
        maxWidth: 140,
        renderCell: (node: VersionLogExpandingArea) => (
            <div style={{maxWidth: '100px',wordWrap: 'break-word', whiteSpace: 'normal'}}>
                <LocalizedTypography text={versionLogTranslationMap[node.fieldName]}  />
            </div>
        )
    },
    {
        id: 'oldValue',
        columnName: window.i18n.automationEngine.AUTOMATION_VERSION_LOG_HEADER_OLD_VALUE,
        minWidth: 0,
        renderCell: (node: VersionLogExpandingArea) => (
            <CellContainer>
                <Typography dangerouslySetInnerHTML={{ __html: translateDisplayValues(node.oldValue) || node.oldValue }} />
            </CellContainer>
        )
    },
    {
        id: 'newValue',
        columnName: window.i18n.automationEngine.AUTOMATION_VERSION_LOG_HEADER_NEW_VALUE,
        minWidth: 0,
        renderCell: (node: VersionLogExpandingArea) => (
            <CellContainer>
                <Typography dangerouslySetInnerHTML={{ __html: translateDisplayValues(node.newValue)  || node.newValue }} />
            </CellContainer>
        ) },
];

export const VersionLogDataTableColumnNames = VersionLogDataTableColumns.map(column => column.columnName)

const TABLE_PER_PAGE_OPTIONS = [
    { value: 10 },
    { value: 25 },
    { value: 50 }
];

const VersionLogSection: FC<VersionLogSectionProps> = ({ selectedAutomationId }) => {
    const { AUTOMATION_ROWS_PER_PAGE, COMMON_VIEW_DATATABLE, AUTOMATION_VERSION_LOG_EXPAND_ROW_LABEL, AUTOMATION_VERSION_LOG_COLLAPSE_ROW_LABEL } = useMultipleLocalizedText();
    const [page, setPage] = useState(0);
    const [perPage, setPerPage] = useState(TABLE_PER_PAGE_OPTIONS[0].value);
    const { data, status } = useVersionLogListQuery({
        companyAutomationId: selectedAutomationId,
        page,
        perPage
    });

    return (
        <VersionLogSectionContainer>
            <LocalizedTypography variant='h3' text={LocalizedText.AUTOMATION_VERSION_LOG} />
            <ListBase
                showSearch={false}
                getRowID={(node: VersionLogSearchResult) => node.actionUserEId}
                pagingContext={{
                    page: page,
                    perPage: perPage,
                    first: page * perPage + 1,
                    last: Math.min(page * perPage + perPage, data?.totalCount ?? 0),
                    hideFirstLastActions: true
                }}
                rowsPerPageLabel={AUTOMATION_ROWS_PER_PAGE}
                resultsPerPage={perPage}
                perPageOptions={TABLE_PER_PAGE_OPTIONS}
                onPageChanged={(e: any, nextPage: number) => setPage(nextPage)}
                onPaginationChange={(e: any) => {
                    setPage(0);
                    setPerPage(e.target.value);
                }}
                results={data || { nodes: [], totalCount: 0 }}
                resultsLoading={status === 'loading'}
                viewContexts={[
                    {
                        ariaLabel: COMMON_VIEW_DATATABLE,
                        icon: <ViewContextTableIcon />,
                        renderResults: (viewContextProps: any) => (
                            <DataTable
                                {...viewContextProps}
                                renderExpand={(versionLog: VersionLogSearchResult) => (
                                    <>
                                        <LocalizedTypography
                                            variant="overline"
                                            text={LocalizedText.AUTOMATION_VERSION_LOG_EDIT_DESCRIPTION}
                                        />
                                        <div style={{maxWidth: '550px'}}>
                                            <Typography dangerouslySetInnerHTML={{ __html: translateDisplayValues(versionLog.editDescription) }} />
                                        </div>
                                        <DataTable
                                            columns={VersionLogDataTableExpandedColumns}
                                            results={{
                                                nodes: versionLog.updatedData,
                                                totalCount: versionLog.updatedData.length
                                            }}
                                            getRowID={(row) => row.fieldName}
                                        />
                                    </>
                                )}
                                expandButtonPosition='right'
                                renderExpandButton={({ isExpanded, handleCollapseClick }) => <Link role='button' className='expand-collapse-row-trigger' onClick={handleCollapseClick}>{isExpanded ? AUTOMATION_VERSION_LOG_COLLAPSE_ROW_LABEL : AUTOMATION_VERSION_LOG_EXPAND_ROW_LABEL}</Link>}
                                labelExpandAriaLabel={AUTOMATION_VERSION_LOG_EXPAND_ROW_LABEL}
                                labelCollapseAriaLabel={AUTOMATION_VERSION_LOG_COLLAPSE_ROW_LABEL}
                                getRowID={(node: VersionLogSearchResult) => node.actionUserEId}
                                columns={VersionLogDataTableColumns}
                                results={data || { nodes: [], totalCount: 0 }}
                            />
                        ),
                        renderLoading: () => (
                            <ListBaseSkeletonLoading
                                columns={VersionLogDataTableColumnNames}
                                items={5}
                            />
                        )
                    }
                ]}
                labelSearch=''
                labelSearchAria=''
                labelFilters=''
                labelClose=''
                labelApplyFilters=''
                labelClearAllFilters=''
                labelResults=''
                labelClear=''
            />
        </VersionLogSectionContainer>
    );
};
export default VersionLogSection;